import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { Compass, X } from '@phosphor-icons/react'
import { useAuthStore } from '@/stores/authStore'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/lib/utils'
import { useTourStore } from './tourStore'
import {
  buildTourSteps,
  placeCard,
  shouldAutoOfferTour,
  type Box,
  type Placement,
  type TourNavGroup,
} from './tourSteps'

// Product tour. One stop per sidebar group; the page recedes with a blur only
// (design-aesthetics §45: no black tint) and the highlighted group stays sharp
// through a clip-path hole. Page-level popovers sit in the z-[80]/[81] band,
// above content and below modals (§59). Controls are h-9 / rounded-md (§53).

const easeOutExpo = [0.16, 1, 0.3, 1] as const
const PAD = 8
const OFFER_DELAY_MS = 1500
const MODAL_RETRY_MS = 1000

const mono: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontWeight: 400,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontSize: '10px',
}
const btnText: React.CSSProperties = { fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }

const surface =
  'bg-white border border-[rgba(15,16,17,0.10)] dark:bg-[#191a1b] dark:border-white/[0.08] rounded-lg'
const primaryBtn =
  'inline-flex items-center justify-center h-9 px-4 rounded-md bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0] text-white transition-colors duration-200'
const secondaryBtn =
  'inline-flex items-center justify-center h-9 px-4 rounded-md bg-white border border-[rgba(15,16,17,0.10)] text-[#3e3e44] hover:bg-[#f5f6f9] hover:border-[rgba(15,16,17,0.18)] dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#d0d6e0] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14] transition-colors duration-200'
const mutedText = 'text-[#5b6068] dark:text-[#8a8f98]'
const strongText = 'text-[#0f1011] dark:text-[#f7f8f8]'
const eyebrowText = 'text-[#8a8f98] dark:text-[#62666d]'

const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches

function shadowFor(theme: string): string {
  return theme === 'dark'
    ? '0 24px 60px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.3)'
    : '0 24px 60px rgba(15,16,17,0.16), 0 2px 6px rgba(15,16,17,0.06)'
}

const IconButton: React.FC<{ label: string; onClick: () => void; className?: string }> = ({
  label,
  onClick,
  className,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={cn(
      'h-7 w-7 flex items-center justify-center rounded-md text-[#8a8f98] hover:text-[#0f1011] hover:bg-[rgba(15,16,17,0.04)] dark:text-[#62666d] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors',
      className,
    )}
  >
    <X className="h-4 w-4" weight="bold" />
  </button>
)

interface ProductTourProps {
  /** The sidebar's own permission-filtered groups: the tour only explains what the user can see. */
  groups: TourNavGroup[]
  /** Opens/closes the mobile sidebar drawer so the tour has something to point at. */
  openMobileSidebar: (open: boolean) => void
}

const ProductTour: React.FC<ProductTourProps> = ({ groups, openMobileSidebar }) => {
  const { user } = useAuthStore()
  const { collapsed, setCollapsed } = useSidebarStore()
  const { actualTheme } = useThemeStore()
  const { pathname } = useLocation()

  const phase = useTourStore((s) => s.phase)
  const index = useTourStore((s) => s.index)
  const storeSteps = useTourStore((s) => s.steps)
  const { setSteps, offerInvite, start, next, back, skip, snooze } = useTourStore.getState()

  const steps = useMemo(() => buildTourSteps(groups), [groups])
  const stepsKey = steps.map((s) => `${s.key}:${s.items.map((i) => i.href).join(',')}`).join('|')

  useEffect(() => {
    setSteps(steps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsKey])

  // ---- first-time invite -------------------------------------------------
  const offeredRef = useRef(false)
  const hasUser = !!user
  useEffect(() => {
    if (phase !== 'idle' || offeredRef.current) return
    if (!hasUser || !shouldAutoOfferTour(user?.tour_state)) return
    if (pathname !== '/dashboard' || steps.length === 0) return
    let timer = 0
    const attempt = () => {
      // Never stack on top of a modal the user is already working in. Keep checking
      // instead of giving up, so the invite still arrives once the modal closes.
      if (document.querySelector('[aria-modal="true"]')) {
        timer = window.setTimeout(attempt, MODAL_RETRY_MS)
        return
      }
      offeredRef.current = true
      offerInvite()
    }
    timer = window.setTimeout(attempt, OFFER_DELAY_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, hasUser, user?.tour_state, pathname, stepsKey])

  // ---- make the sidebar visible for the tour, then put it back ------------
  const restoreCollapsed = useRef(false)
  const openedDrawer = useRef(false)
  useEffect(() => {
    if (phase === 'touring') {
      if (collapsed) {
        restoreCollapsed.current = true
        setCollapsed(false)
      }
      if (!isDesktop()) {
        openedDrawer.current = true
        openMobileSidebar(true)
      }
      return
    }
    if (restoreCollapsed.current) {
      restoreCollapsed.current = false
      setCollapsed(true)
    }
    if (openedDrawer.current) {
      openedDrawer.current = false
      openMobileSidebar(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  // ---- locate the highlighted group ---------------------------------------
  const step = storeSteps[index]
  const [rect, setRect] = useState<Box | null>(null)
  useEffect(() => {
    if (phase !== 'touring' || !step) {
      setRect(null)
      return
    }
    const find = () => document.querySelector<HTMLElement>(`[data-tour="${step.key}"]`)
    const measure = () => {
      const el = find()
      if (!el) return false
      el.scrollIntoView({ block: 'nearest' })
      const r = el.getBoundingClientRect()
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height })
      return true
    }
    if (!measure()) {
      // The target is not in the DOM (for example hidden by the viewport layout): move on.
      void next()
      return
    }
    // The sidebar width animates when it is expanded for the tour; re-measure once it settles.
    const settle = window.setTimeout(measure, 380)
    const el = find()
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(measure) : null
    if (el && ro) ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      window.clearTimeout(settle)
      ro?.disconnect()
      window.removeEventListener('resize', measure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, index, step?.key])

  // ---- card placement ------------------------------------------------------
  const cardRef = useRef<HTMLDivElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  const [place, setPlace] = useState<Placement | null>(null)
  // The card appears in place on the first stop and only glides between later stops.
  const [glide, setGlide] = useState(false)
  useEffect(() => {
    if (phase !== 'touring') {
      setPlace(null)
      setGlide(false)
    }
  }, [phase])
  useLayoutEffect(() => {
    if (phase !== 'touring' || !rect || !cardRef.current) return
    if (!glide) {
      const id = window.requestAnimationFrame(() => setGlide(true))
      return () => window.cancelAnimationFrame(id)
    }
  }, [phase, rect, glide])
  useLayoutEffect(() => {
    if (phase !== 'touring' || !rect || !cardRef.current) return
    setPlace(
      placeCard(
        rect,
        { width: cardRef.current.offsetWidth, height: cardRef.current.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        { pad: PAD },
      ),
    )
  }, [phase, rect, index])

  // ---- focus + keyboard while touring ---------------------------------------
  const returnFocus = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (phase !== 'touring') return
    returnFocus.current = (document.activeElement as HTMLElement) ?? null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        void skip()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        void next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        back()
      } else if (e.key === 'Tab' && cardRef.current) {
        const focusable = Array.from(cardRef.current.querySelectorAll<HTMLElement>('button, a[href]')).filter(
          (el) => el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        const active = document.activeElement
        if (!cardRef.current.contains(active) || (e.shiftKey && active === first)) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      returnFocus.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  useEffect(() => {
    if (phase === 'touring' && rect) nextRef.current?.focus({ preventScroll: true })
    // Move focus only when the stop changes, not on every re-measure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, index, rect !== null])

  if (typeof document === 'undefined') return null

  const total = storeSteps.length
  const isLast = index === total - 1
  const shadow = shadowFor(actualTheme)

  let clip = 'none'
  if (rect) {
    const x1 = rect.left - PAD
    const y1 = rect.top - PAD
    const x2 = rect.left + rect.width + PAD
    const y2 = rect.top + rect.height + PAD
    clip = `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${x1}px ${y1}px, ${x1}px ${y2}px, ${x2}px ${y2}px, ${x2}px ${y1}px, ${x1}px ${y1}px)`
  }

  return createPortal(
    <>
      {/* Invite: small, non-blocking, no backdrop. The page stays usable. */}
      <AnimatePresence>
        {phase === 'invite' && (
          <motion.div
            key="invite"
            role="region"
            aria-label="Product tour invitation"
            className={cn(surface, 'fixed z-[80] bottom-5 right-3 left-3 sm:left-auto sm:right-5 sm:w-[316px] p-4')}
            style={{ boxShadow: shadow }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.4, ease: easeOutExpo }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') void snooze()
            }}
          >
            <IconButton label="Dismiss tour invitation" onClick={() => void snooze()} className="absolute right-2 top-2" />
            <div className="flex items-start gap-3 pr-6">
              <div className="relative h-9 w-9 shrink-0 rounded-md bg-[#f5f6f9] border border-[rgba(15,16,17,0.06)] dark:bg-white/[0.02] dark:border-white/[0.05] flex items-center justify-center">
                <Compass className="h-[18px] w-[18px] text-[#5e6ad2] dark:text-[#7170ff]" weight="regular" aria-hidden="true" />
                <span
                  aria-hidden="true"
                  className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#5e6ad2] dark:bg-[#7170ff] ring-2 ring-white dark:ring-[#191a1b]"
                />
              </div>
              <div>
                <p className={strongText} style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.182px' }}>
                  New here? Take a quick tour
                </p>
                <p className={cn(mutedText, 'mt-0.5')} style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
                  About a minute. See what each sidebar tab is for.
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button type="button" className={primaryBtn} style={btnText} onClick={() => start('invite')}>
                Start tour
              </button>
              <button
                type="button"
                className={cn(
                  'h-9 px-3 rounded-md transition-colors hover:bg-[rgba(15,16,17,0.04)] dark:hover:bg-white/[0.04]',
                  mutedText,
                )}
                style={btnText}
                onClick={() => void snooze()}
              >
                Not now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {phase === 'touring' && step && (
        <>
          {/* Blur-only veil with a sharp hole around the highlighted group. */}
          <div
            aria-hidden="true"
            className="fixed inset-0 z-[80] motion-reduce:transition-none"
            style={{ backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', clipPath: clip }}
          />
          {rect && (
            // Also swallows clicks in the hole so the sidebar cannot be navigated mid-tour.
            <div
              aria-hidden="true"
              className="fixed z-[80] rounded-xl border-[1.5px] border-[#5e6ad2] dark:border-[#7170ff] shadow-[0_0_0_4px_rgba(94,106,210,0.12)] transition-all duration-[450ms] motion-reduce:transition-none"
              style={{
                left: rect.left - PAD,
                top: rect.top - PAD,
                width: rect.width + PAD * 2,
                height: rect.height + PAD * 2,
                transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
          )}

          <div
            ref={cardRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-tour-title"
            aria-describedby="product-tour-body"
            className={cn(
              surface,
              'fixed z-[81] w-[352px] max-w-[calc(100vw-24px)] p-[18px] pb-3 duration-[450ms] motion-reduce:transition-none',
              place ? 'opacity-100' : 'opacity-0',
            )}
            style={{
              left: place?.left ?? -9999,
              top: place?.top ?? -9999,
              boxShadow: shadow,
              transitionProperty: glide ? 'top, left, opacity' : 'opacity',
              transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {place?.arrowTop != null && (
              <span
                aria-hidden="true"
                className="absolute -left-[6px] h-[11px] w-[11px] rotate-45 bg-white dark:bg-[#191a1b] border-l border-b border-[rgba(15,16,17,0.10)] dark:border-white/[0.08]"
                style={{ top: place.arrowTop }}
              />
            )}

            <IconButton label="Close and skip tour" onClick={() => void skip()} className="absolute right-2.5 top-2.5" />

            <p className={eyebrowText} style={mono}>
              {step.eyebrow} · {index + 1} of {total}
            </p>
            <h2
              id="product-tour-title"
              className={cn(strongText, 'mt-2 mb-3 pr-6')}
              style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
            >
              {step.title}
            </h2>

            <ul id="product-tour-body" className="mb-3 space-y-0.5">
              {step.items.map((item) => {
                const Icon = item.icon
                return (
                  <li
                    key={item.href}
                    className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-[rgba(15,16,17,0.03)] dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <span className="h-8 w-8 shrink-0 rounded-md bg-[#f5f6f9] border border-[rgba(15,16,17,0.06)] dark:bg-white/[0.02] dark:border-white/[0.05] flex items-center justify-center">
                      {Icon && <Icon className="h-4 w-4 text-[#5e6ad2] dark:text-[#7170ff]" />}
                    </span>
                    <span className="min-w-0">
                      <span className={cn(strongText, 'block')} style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                        {item.name}
                      </span>
                      <span className={cn(mutedText, 'block mt-px')} style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.13px' }}>
                        {item.purpose}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>

            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[rgba(15,16,17,0.06)] dark:border-white/[0.06]">
              <div className="flex items-center gap-2" aria-hidden="true">
                <div className="flex items-center gap-1.5">
                  {storeSteps.map((s, i) => (
                    <span
                      key={s.key}
                      className={cn(
                        'h-1.5 rounded-full transition-all duration-300',
                        i === index
                          ? 'w-[18px] bg-[#5e6ad2] dark:bg-[#7170ff]'
                          : i < index
                            ? 'w-1.5 bg-[#5e6ad2]/35 dark:bg-[#7170ff]/35'
                            : 'w-1.5 bg-[#c0c4cb] dark:bg-[#3e3e44]',
                      )}
                    />
                  ))}
                </div>
              </div>
              <span className="sr-only" aria-live="polite">
                Step {index + 1} of {total}: {step.title}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={cn(secondaryBtn, index === 0 && 'invisible')}
                  style={btnText}
                  onClick={() => back()}
                >
                  Back
                </button>
                <button ref={nextRef} type="button" className={primaryBtn} style={btnText} onClick={() => void next()}>
                  {isLast ? 'Done' : 'Next'}
                </button>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => void skip()}
                className={cn(
                  'h-7 px-1 rounded-md underline underline-offset-4 decoration-[rgba(15,16,17,0.18)] dark:decoration-white/[0.14] hover:text-[#0f1011] dark:hover:text-[#f7f8f8] transition-colors',
                  eyebrowText,
                )}
                style={{ fontSize: '12px', fontWeight: 510 }}
              >
                Skip tour
              </button>
              <span className={cn(eyebrowText, 'hidden sm:inline')} style={{ fontSize: '11px' }}>
                Use arrow keys to move, Esc to skip
              </span>
            </div>
          </div>
        </>
      )}
    </>,
    document.body,
  )
}

export default ProductTour
